import {FC, useEffect, useState} from 'react'
import moment from 'moment'
import Swal from 'sweetalert2'
import {PageTitle} from '../../../../_metronic/layout/core'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {Button} from '../../../components/Button/Button'
import {Datatable} from '../../../components/Datatable/Datatable'
import {Input} from '../../../components/Input/Input'
import {Loading} from '../../../components/Loading/Loading'
import {ModalWrapper} from '../../../components/Modals/General/ModalWrapper/ModalWrapper'
import useModals from '../../../hooks/useModals'
import {DELETE, GET, POST, PUT} from '../../../services/api'
import uuid from 'react-uuid'
import styles from './hotelpoints.module.css'

type RewardKind = 'physical' | 'service' | 'courtesy' | 'upgrade'

type RewardForm = {
  title: string
  description: string
  image: string
  pointsCost: number | string
  stock: string
  quantityPerCustomer: string
  kind: RewardKind
  status: boolean
  dateInit: string
  dateEnd: string
  requiresActiveReservation: boolean
}

type RewardRow = RewardForm & {
  id: number
  pointsCost: number
  stockDisplay: string
  kindLabel: string
  statusLabel: string
  status: boolean
  dateInit: string | null
  dateEnd: string | null
}

const KIND_OPTIONS: Array<{
  value: RewardKind
  select: string
  description: string
}> = [
  {
    value: 'physical',
    select: 'Físico',
    description: 'Item entregue na recepção, como brinde, produto ou souvenir.',
  },
  {
    value: 'service',
    select: 'Serviço',
    description: 'Serviço ou experiência que a equipe agenda e presta ao hóspede.',
  },
  {
    value: 'courtesy',
    select: 'Cortesia',
    description: 'Cortesia de consumo ou amenidade concedida durante a estadia.',
  },
  {
    value: 'upgrade',
    select: 'Upgrade',
    description: 'Melhoria de categoria (quarto ou experiência), sujeita à disponibilidade.',
  },
]

const KIND_LABELS: Record<RewardKind, string> = {
  physical: 'Físico',
  service: 'Serviço',
  courtesy: 'Cortesia',
  upgrade: 'Upgrade',
}

const DEFAULT_FORM: RewardForm = {
  title: '',
  description: '',
  image: '',
  pointsCost: 0,
  stock: '',
  quantityPerCustomer: '1',
  kind: 'physical',
  status: true,
  dateInit: '',
  dateEnd: '',
  requiresActiveReservation: false,
}

const extractList = (response: any): any[] => {
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.dados)) return response.dados
  if (Array.isArray(response)) return response
  return []
}

const toDateOnly = (value: string | null) => {
  if (!value) return ''
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
}

const toOptionalInt = (value: string): number | null => {
  if (value === '' || value === undefined || value === null) return null
  const parsed = parseInt(String(value), 10)
  return Number.isFinite(parsed) ? parsed : null
}

const RewardsContent: FC = () => {
  const {showWrapperModal, closeWrapperModal} = useModals()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [rewards, setRewards] = useState<RewardRow[]>([])
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<RewardForm>(DEFAULT_FORM)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [imageFile, setImageFile] = useState<File | string | null>(null)
  const [originalImage, setOriginalImage] = useState<string>('')

  const setField = <K extends keyof RewardForm>(field: K, value: RewardForm[K]) => {
    setForm((prev) => ({...prev, [field]: value}))
    if (errors[field as string]) {
      const next = {...errors}
      delete next[field as string]
      setErrors(next)
    }
  }

  const loadRewards = async () => {
    setLoading(true)
    try {
      const response = await GET('/hotel-points/rewards/admin')
      const rows = extractList(response).map((item: any) => ({
        id: Number(item?.id || 0),
        title: item?.title || '-',
        description: item?.description || '',
        image: item?.image || '',
        pointsCost: Number(item?.pointsCost || 0),
        stock: item?.stock === null || item?.stock === undefined ? '' : String(item.stock),
        stockDisplay:
          item?.stock === null || item?.stock === undefined ? 'Ilimitado' : String(item.stock),
        quantityPerCustomer:
          item?.quantityPerCustomer === null || item?.quantityPerCustomer === undefined
            ? ''
            : String(item.quantityPerCustomer),
        kind: (item?.kind || 'physical') as RewardKind,
        kindLabel: KIND_LABELS[(item?.kind || 'physical') as RewardKind] || item?.kind,
        status: Boolean(item?.status),
        statusLabel: item?.status ? 'Ativo' : 'Inativo',
        dateInit: item?.dateInit || null,
        dateEnd: item?.dateEnd || null,
        requiresActiveReservation: Boolean(item?.requiresActiveReservation),
      }))
      setRewards(rows)
    } catch (error) {
      console.error(error)
      setRewards([])
      Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: 'Erro ao carregar as recompensas.',
      })
    } finally {
      setLoading(false)
    }
  }

  const resetModalState = () => {
    setEditingId(null)
    setForm(DEFAULT_FORM)
    setErrors({})
    setImageFile(null)
    setOriginalImage('')
  }

  const openCreateModal = () => {
    setEditingId(null)
    setForm(DEFAULT_FORM)
    setErrors({})
    setImageFile(null)
    setOriginalImage('')
    showWrapperModal('Nova recompensa')
  }

  const openEditModal = (item: RewardRow) => {
    setEditingId(item.id)
    setForm({
      title: item.title === '-' ? '' : item.title,
      description: item.description,
      image: item.image,
      pointsCost: item.pointsCost,
      stock: item.stock,
      quantityPerCustomer: item.quantityPerCustomer,
      kind: item.kind,
      status: item.status,
      dateInit: toDateOnly(item.dateInit),
      dateEnd: toDateOnly(item.dateEnd),
      requiresActiveReservation: item.requiresActiveReservation,
    })
    setErrors({})
    setImageFile(item.image || null)
    setOriginalImage(item.image || '')
    showWrapperModal('Editar recompensa')
  }

  const validateForm = () => {
    const next: Record<string, string> = {}
    if (!form.title.trim()) next.title = 'Informe o título.'
    const pointsCost = Number(form.pointsCost)
    if (!Number.isInteger(pointsCost) || pointsCost < 1) {
      next.pointsCost = 'Informe um custo inteiro maior que zero.'
    }
    if (form.stock !== '') {
      const stock = Number(form.stock)
      if (!Number.isInteger(stock) || stock < 0) next.stock = 'Informe um limite inteiro válido.'
    }
    if (form.quantityPerCustomer !== '') {
      const qty = Number(form.quantityPerCustomer)
      if (!Number.isInteger(qty) || qty < 1) {
        next.quantityPerCustomer = 'Informe uma quantidade inteira maior que zero.'
      }
    }
    const start = form.dateInit ? moment(form.dateInit) : null
    const end = form.dateEnd ? moment(form.dateEnd) : null
    if (start && end && start.isValid() && end.isValid() && end.isBefore(start, 'day')) {
      next.dateEnd = 'A data final deve ser igual ou posterior à data inicial.'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const saveReward = async () => {
    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Campos obrigatórios',
        text: 'Verifique os campos destacados antes de salvar.',
      })
      return
    }

    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      image: form.image.trim() || null,
      pointsCost: Number(form.pointsCost),
      stock: toOptionalInt(form.stock),
      quantityPerCustomer: toOptionalInt(form.quantityPerCustomer),
      kind: form.kind,
      status: Boolean(form.status),
      dateInit: form.dateInit ? moment.utc(form.dateInit).startOf('day').toISOString() : null,
      dateEnd: form.dateEnd ? moment.utc(form.dateEnd).endOf('day').toISOString() : null,
      requiresActiveReservation: Boolean(form.requiresActiveReservation),
    }

    try {
      setSaving(true)

      let imageUrl = originalImage || form.image.trim() || null
      if (imageFile instanceof File) {
        if (originalImage) {
          try {
            await POST('/upload-images/delete-one', {url: originalImage})
          } catch (error) {
            console.error(error)
          }
        }
        const uploadData = new FormData()
        uploadData.append('file', imageFile)
        uploadData.append('nameImage', `hotel-points-reward-${uuid()}.jpg`)
        uploadData.append('originalSize', 'true')
        const imageRes = await POST('/upload-images/upload-one', uploadData)
        if (imageRes?.success && imageRes?.image) {
          imageUrl = imageRes.image
        }
      } else if (!imageFile) {
        imageUrl = null
      }

      payload.image = imageUrl
      const response = editingId
        ? await PUT(`/hotel-points/rewards/${editingId}`, payload)
        : await POST('/hotel-points/rewards', payload)

      if (response?.success === false) {
        throw new Error(response?.message || 'Não foi possível salvar a recompensa.')
      }

      closeWrapperModal()
      resetModalState()
      await loadRewards()
      Swal.fire({
        icon: 'success',
        title: 'Recompensa salva',
        text: 'O registro foi salvo com sucesso.',
      })
    } catch (error) {
      console.error(error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: String(error) || 'Não foi possível salvar a recompensa.',
      })
    } finally {
      setSaving(false)
    }
  }

  const deleteReward = async (item: RewardRow) => {
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Excluir recompensa?',
      text: 'Essa ação não pode ser desfeita.',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
    })
    if (!confirmation.isConfirmed) return

    try {
      setLoading(true)
      await DELETE(`/hotel-points/rewards/${item.id}`)
      await loadRewards()
      Swal.fire({icon: 'success', title: 'Recompensa removida'})
    } catch (error) {
      console.error(error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível excluir a recompensa.',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRewards()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Recompensas</span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  Catálogo de recompensas resgatáveis com Pontos Hotel.
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text='Nova recompensa'
                  color='primary'
                  size='small'
                  icon='fas fa-plus'
                  horizontalPadding={6}
                  btnAttr={{type: 'button'}}
                  click={openCreateModal}
                  btnClass='fw-bolder'
                />
              </div>
            </div>
            <div className='card-body'>
              <Datatable
                data={rewards}
                headers={[
                  {key: 'Título', value: 'title', type: 'default'},
                  {key: 'Tipo', value: 'kindLabel', type: 'default'},
                  {key: 'Pontos', value: 'pointsCost', type: 'default'},
                  {key: 'Limite', value: 'stockDisplay', type: 'default'},
                  {key: 'Status', value: 'statusLabel', type: 'default'},
                  {key: 'Data inicial', value: 'dateInit', type: 'date'},
                  {key: 'Data final', value: 'dateEnd', type: 'date'},
                  {key: 'Ações', value: 'acoes', type: '', noOrder: true},
                ]}
                options={[
                  {
                    icon: 'fas fa-pen',
                    action: (item) => openEditModal(item),
                    title: 'Editar recompensa',
                  },
                  {
                    icon: 'fas fa-trash',
                    action: (item) => void deleteReward(item),
                    title: 'Excluir recompensa',
                  },
                ]}
                color='primary'
                showControls
              />
            </div>
          </div>
        </div>
      )}

      <ModalWrapper
        confirmAction={() => void saveReward()}
        shouldCloseModal={false}
        disableMainButton={saving}
        onCloseAction={resetModalState}
        textAction={saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Salvar'}
        width={70}
      >
        <div className='d-flex flex-column gap-8 mt-4'>
          <div>
            <h6 className='fw-bold text-gray-800 mb-3'>Identificação</h6>
            <div className='row g-4'>
              <div className='col-12'>
                <Input
                  inputAttr={{type: 'text'}}
                  label='Título'
                  value={form.title}
                  change={(value: string) => setField('title', value)}
                  required
                  marginTop='0'
                />
                {errors.title && <div className='text-danger small mt-1'>{errors.title}</div>}
              </div>
              <div className='col-12'>
                <Input
                  inputAttr={{type: 'textarea'}}
                  label='Descrição'
                  value={form.description}
                  change={(value: string) => setField('description', value)}
                  rows={3}
                  marginTop='0'
                />
              </div>
            </div>
          </div>

          <div>
            <h6 className='fw-bold text-gray-800 mb-1'>Tipo da recompensa</h6>
            <p className='text-muted small mb-3'>
              Escolha como o hotel entrega o benefício após o resgate.
            </p>
            <div className='row g-3'>
              {KIND_OPTIONS.map((option) => {
                const active = form.kind === option.value
                return (
                  <div className='col-md-6' key={option.value}>
                    <div
                      className={`${styles.kindCard} ${active ? styles.kindCardActive : ''}`}
                      onClick={() => setField('kind', option.value)}
                    >
                      <div className='d-flex align-items-center justify-content-between mb-1'>
                        <span className='fw-bold text-gray-800'>{option.select}</span>
                        {active ? (
                          <span className='badge badge-light-primary'>Selecionado</span>
                        ) : null}
                      </div>
                      <div className='text-muted small'>{option.description}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div>
            <h6 className='fw-bold text-gray-800 mb-3'>Imagem</h6>
            <Input
              inputAttr={{type: 'image'}}
              label='Imagem da recompensa'
              value={imageFile}
              change={(value: React.BaseSyntheticEvent | null) => {
                if (!value || !value.target) {
                  setImageFile(null)
                  setField('image', '')
                  return
                }
                const file = value.target.files?.[0]
                setImageFile(file || null)
              }}
            />
            <div className='text-muted small mt-2'>
              Selecione um arquivo do computador. Formatos: jpg, jpeg, png ou gif.
            </div>
          </div>

          <div>
            <h6 className='fw-bold text-gray-800 mb-3'>Pontos e limites</h6>
            <div className='row g-4'>
              <div className='col-md-4'>
                <Input
                  inputAttr={{type: 'number'}}
                  label='Custo em pontos'
                  value={String(form.pointsCost)}
                  change={(value: string) => setField('pointsCost', parseInt(value, 10) || 0)}
                  minNumber={1}
                  allowFloat={false}
                  marginTop='0'
                />
                {errors.pointsCost && (
                  <div className='text-danger small mt-1'>{errors.pointsCost}</div>
                )}
              </div>
              <div className='col-md-4'>
                <Input
                  inputAttr={{type: 'number'}}
                  label='Limite'
                  value={form.stock}
                  change={(value: string) => setField('stock', value)}
                  minNumber={0}
                  allowFloat={false}
                  marginTop='0'
                />
                <div className='text-muted small mt-1'>Deixe vazio para ser ilimitado.</div>
                {errors.stock && <div className='text-danger small mt-1'>{errors.stock}</div>}
              </div>
              <div className='col-md-4'>
                <Input
                  inputAttr={{type: 'number'}}
                  label='Quantidade por cliente'
                  value={form.quantityPerCustomer}
                  change={(value: string) => setField('quantityPerCustomer', value)}
                  minNumber={1}
                  allowFloat={false}
                  marginTop='0'
                />
                {errors.quantityPerCustomer && (
                  <div className='text-danger small mt-1'>{errors.quantityPerCustomer}</div>
                )}
              </div>
            </div>
          </div>

          <div>
            <h6 className='fw-bold text-gray-800 mb-3'>Disponibilidade</h6>
            <div className='row g-4'>
              <div className='col-md-6'>
                <Input
                  inputAttr={{type: 'select'}}
                  label='Status'
                  value={String(form.status)}
                  change={(value: string) => setField('status', value === 'true')}
                  options={[
                    {select: 'Ativo', value: 'true'},
                    {select: 'Inativo', value: 'false'},
                  ]}
                  marginTop='0'
                />
              </div>
              <div className='col-md-6'>
                <Input
                  inputAttr={{type: 'select'}}
                  label='Exige reserva ativa'
                  value={String(form.requiresActiveReservation)}
                  change={(value: string) =>
                    setField('requiresActiveReservation', value === 'true')
                  }
                  options={[
                    {select: 'Sim', value: 'true'},
                    {select: 'Não', value: 'false'},
                  ]}
                  marginTop='0'
                />
              </div>
              <div className='col-md-6'>
                <Input
                  inputAttr={{type: 'date'}}
                  label='Data inicial'
                  value={form.dateInit}
                  change={(value: string) => setField('dateInit', value)}
                  marginTop='0'
                />
              </div>
              <div className='col-md-6'>
                <Input
                  inputAttr={{type: 'date'}}
                  label='Data final'
                  value={form.dateEnd}
                  change={(value: string) => setField('dateEnd', value)}
                  marginTop='0'
                />
                {errors.dateEnd && <div className='text-danger small mt-1'>{errors.dateEnd}</div>}
              </div>
            </div>
          </div>
        </div>
      </ModalWrapper>
    </>
  )
}

const HotelPointsRewards: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: 'Pontos Hotel', path: 'hotel-points', isActive: false},
          {title: 'Recompensas', path: 'hotel-points/rewards', isActive: true},
        ]}
      >
        Recompensas
      </PageTitle>
      <RewardsContent />
    </>
  )
}

export {HotelPointsRewards}
